import React, { useState, useEffect, useCallback } from 'react';
import { scrollProgress } from '../../context/ScrollContext';
import { useTheme } from '../../context/ThemeContext';
import { SECTIONS } from '../../hooks/useScrollSection';

const NAV_LINKS = [
  { label: 'Waymaker', sectionId: 'waymaker' },
  { label: 'Pulse Engine', sectionId: 'pulse-engine' },
  { label: 'Celestial Drift', sectionId: 'celestial-drift' },
  { label: 'FormCraft', sectionId: 'formcraft' },
  { label: 'Ember', sectionId: 'ember-chronicles' },
  { label: 'Code Forge', sectionId: 'code-forge' },
];

const NavigationCapsule = () => {
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState('intro');
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    let frameId;
    const update = () => {
      const t = scrollProgress.value || 0;
      setVisible(t > 0.05);

      /* Determine which section is active */
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        if (t >= SECTIONS[i].start) {
          setActiveId(SECTIONS[i].id);
          break;
        }
      }
      frameId = requestAnimationFrame(update);
    };
    frameId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frameId);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const section = SECTIONS.find((s) => s.id === sectionId);
    if (section) {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const targetScroll = section.start * scrollHeight;
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  }, []);

  return (
    <div
      className="glass-elevated"
      style={{
        position: 'fixed',
        top: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        borderRadius: '100px',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.4s ease, transform 0.4s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* Wordmark */}
      <span
        style={{
          fontWeight: 700,
          letterSpacing: '0.15em',
          fontSize: '0.82rem',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
        onClick={() => scrollToSection('intro')}
      >
        LAZPLAY
      </span>

      {/* Center nav links */}
      <div className="nav-links-center" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        {NAV_LINKS.map((link, i) => (
          <React.Fragment key={link.label}>
            <span
              className="text-caption"
              style={{
                cursor: 'pointer',
                color: activeId === link.sectionId ? 'var(--accent)' : 'var(--text-muted)',
                fontWeight: activeId === link.sectionId ? 600 : 500,
                transition: 'color 0.2s ease',
                whiteSpace: 'nowrap',
                fontSize: '0.72rem',
              }}
              onClick={() => scrollToSection(link.sectionId)}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                if (activeId !== link.sectionId) {
                  e.currentTarget.style.color = 'var(--text-muted)';
                }
              }}
            >
              {link.label}
            </span>
            {i < NAV_LINKS.length - 1 && (
              <span style={{ color: 'var(--border-strong)', fontSize: '0.55rem' }}>·</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Right controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          className="nav-control-btn"
          title="Toggle theme"
          onClick={toggleTheme}
          style={{
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
            transition: 'color 0.3s',
          }}
        >
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </div>

      <style>
        {`
          @media (max-width: 860px) {
            .nav-links-center {
              display: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default NavigationCapsule;
